import { useState, useEffect, useMemo } from 'react'
import { checkAvailability } from '../services/inventoryService'
import { StoreInventory } from '../DeShippingTableWrapper'

interface UseInventoryDataProps {
  variantId: string
  quantity: number
  enabled?: boolean // Optional flag to control if the request should run
}

interface UseInventoryDataReturn {
  loading: boolean
  error: string | null
  data: StoreInventory[] | null
  hasInsufficientInventory: boolean // Whether any selected store has insufficient stock
  refetch: () => Promise<StoreInventory[] | null>
}

export function useInventoryData({ 
  variantId, 
  quantity, 
  enabled = true 
}: UseInventoryDataProps): UseInventoryDataReturn {
  const [state, setState] = useState<{
    loading: boolean
    error: string | null
    data: StoreInventory[] | null
  }>({ loading: false, error: null, data: null })

  const fetchInventory = async (): Promise<StoreInventory[] | null> => {
    // Don't fetch if disabled or missing required params
    if (!enabled || !variantId || quantity <= 0) {
      return null
    }
    
    try {
      setState(prev => ({ ...prev, loading: true, error: null }))
      const data = await checkAvailability(variantId, quantity)
      setState({ loading: false, error: null, data })
      return data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error fetching inventory data'
      setState({ loading: false, error: errorMessage, data: null })
      console.error('Error in useInventoryData:', error)
      return null
    }
  }

  // Fetch data when inputs change
  useEffect(() => {
    fetchInventory()
  }, [variantId, quantity, enabled])

  // Compute whether any store has insufficient inventory
  const hasInsufficientInventory = useMemo(() => {
    if (!state.data) return false
    return state.data.some(store => !store.hasEnoughInventory)
  }, [state.data])

  return {
    ...state,
    hasInsufficientInventory,
    refetch: fetchInventory
  }
}